import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DualLangInputComponent } from '../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { DualLangTextAreaComponent } from '../../../../../../form-builder/lib/dual-lang-text-area/dual-lang-text-area.component';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs';
import { inputTypesEnum } from '../../../../../../form-builder/enums/input-type';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { CheckboxComponent } from '../../../../../../ui-components/lib/checkbox/checkbox.component';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import {
  AssignList,
  LookupOptionItem,
} from '../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { MultiLookupFieldComponent } from '../../../../../../ui-components/lib/multi-lookup-field/multi-lookup-field.component';
import { MultiSelectComponent } from '../../../../../../ui-components/lib/multi-select/multi-select.component';
import {
  OptionItem,
  ToggleOption,
} from '../../../../../../ui-components/lib/option.interface';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ToggleComponent } from '../../../../../../ui-components/lib/toggle/toggle.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { localized } from '../../custom-workflow/custom-workflow.model';
import { ReflectionParamTypeEnum } from '../enums/reflection-param-type.enum';
import { ReflectionRecipientTypeEnum } from '../enums/reflection-recipient-type.enum';
import { ReflectionScopeEnum } from '../enums/reflection-scope.enum';
import {
  buildMapRow,
  buildRecipientRow,
  isParamVisible,
} from '../reflection-form.factory';
import { ReflectionField, ReflectionParam } from '../reflections.models';
import {
  REFLECTION_USER_LOOKUP_API,
  reflectionUserLookupAssignList,
  userLookupOptions,
} from '../reflection-user-lookup';

const SINGLE_CONTROL_TYPES = new Set([
  ReflectionParamTypeEnum.FIELD_KEY,
  ReflectionParamTypeEnum.FIELD_KEY_LIST,
  ReflectionParamTypeEnum.ENUM,
  ReflectionParamTypeEnum.MULTI_ENUM,
  ReflectionParamTypeEnum.TEXT,
  ReflectionParamTypeEnum.NUMBER,
]);

type RawValue = Record<string, unknown>;
type PlaceholderLocale = 'en' | 'ar';

/** One recipient row with what the template needs to render it. */
interface RecipientRowView {
  group: FormGroup;
  index: number;
  type: ReflectionRecipientTypeEnum | null;
  selectedUsers: LookupOptionItem[];
}

/** Renders the input for one `ReflectionParam` and binds it to `group.controls[param.key]`. */
@Component({
  selector: 'lib-reflection-param-input',
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslocoPipe,
    DualLangInputComponent,
    DualLangTextAreaComponent,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    MultiLookupFieldComponent,
    MultiSelectComponent,
    SelectComponent,
    ToggleComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './reflection-param-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionParamInputComponent {
  private readonly transloco = inject(TranslocoService);

  param = input.required<ReflectionParam>();
  group = input.required<FormGroup>();
  fields = input.required<ReflectionField[]>();
  placeholders = input<{ value: string; label: string }[]>([]);
  scope = input.required<ReflectionScopeEnum>();
  /** A secret already has a stored value (the editor shows "Saved · Replace"). */
  hasSecret = input(false);
  /**
   * Names of the users already stored in the config, by id (the config keeps ids only). Must be set before the
   * lookup renders, as it reads its selection once.
   */
  userLabels = input<Record<number, string>>({});
  /** Prefix of the element ids, so nested rows stay unique. */
  idPrefix = input('reflection_param');

  readonly ParamType = ReflectionParamTypeEnum;
  readonly RecipientType = ReflectionRecipientTypeEnum;
  /** Fallback locale for inserts when no dual-language text area is rendered (e.g. in logic-only tests). */
  readonly activeLocale = signal<PlaceholderLocale>('en');

  /** The dual-language text area of a localized_text param; its EN/AR switch decides where a placeholder goes. */
  private readonly localizedText = viewChild(DualLangTextAreaComponent);

  /** Throwaway control of the placeholder picker; picking a value inserts it and resets the picker. */
  readonly pickerForm = new FormGroup({
    token: new FormControl<string | null>(null),
  });

  constructor() {
    // React to the picker's value, not to lib-select's (changeOption): lib-select emits that twice per click
    // (once directly and once from its own valueChanges), which inserted every placeholder twice.
    this.pickerForm.controls.token.valueChanges
      .pipe(
        filter((token): token is string => !!token),
        takeUntilDestroyed()
      )
      .subscribe(token => this.pickPlaceholder(token));
  }

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  /** Raw value of the whole group, re-read on every change (and when the group input is swapped). */
  private readonly groupValue = toSignal(
    toObservable(this.group).pipe(
      switchMap(group =>
        group.valueChanges.pipe(
          startWith(null),
          map(() => group.getRawValue() as RawValue)
        )
      ),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ),
    { initialValue: null }
  );
  private readonly rawValue = computed(
    () => this.groupValue() ?? (this.group().getRawValue() as RawValue)
  );

  readonly visible = computed(() =>
    isParamVisible(this.param(), this.rawValue())
  );
  readonly controlId = computed(() => `${this.idPrefix()}_${this.param().key}`);
  /** Params bound to a single control show its validation message; groups and arrays are checked by the editor. */
  readonly showValidation = computed(() =>
    SINGLE_CONTROL_TYPES.has(this.param().type)
  );
  readonly label = computed(() => localized(this.param().label, this.lang()));
  readonly hint = computed(() => localized(this.param().hint, this.lang()));

  private fieldChoices(fields: ReflectionField[]): OptionItem[] {
    return fields.map(field => ({
      value: field.key,
      label: localized(field.label, this.lang()),
    }));
  }

  /** Fields the param accepts (all of them when it names no types). */
  readonly fieldOptions = computed<OptionItem[]>(() => {
    const allowed = this.param().allowed_field_types;
    return this.fieldChoices(
      this.fields().filter(
        field => !allowed.length || allowed.includes(field.type)
      )
    );
  });

  readonly enumOptions = computed<OptionItem[]>(() =>
    this.param().options.map(option => ({
      value: option.value,
      label: localized(option.label, this.lang()),
    }))
  );

  readonly checkboxOptions = computed<ToggleOption[]>(() =>
    this.enumOptions().map(option => ({
      value: option.value as string,
      label: option.label,
    }))
  );

  readonly toggleOption = computed<ToggleOption>(() => ({
    label: this.label(),
    value: true,
  }));

  readonly recipientTypeOptions = computed<OptionItem[]>(() => {
    this.lang();
    const stepScope = this.scope() === ReflectionScopeEnum.STEP;
    return Object.values(ReflectionRecipientTypeEnum)
      .filter(
        type => stepScope || type !== ReflectionRecipientTypeEnum.STEP_ASSIGNEES
      )
      .map(type => ({
        value: type,
        label: this.transloco.translate(`reflection_recipient_type_${type}`),
      }));
  });

  /** A `user_field` recipient reads its users from a user lookup field. */
  readonly userFieldOptions = computed<OptionItem[]>(() =>
    this.fieldChoices(
      this.fields().filter(field => field.type === inputTypesEnum.LOOKUP_USER)
    )
  );

  /** Built-in placeholders, then the template fields, as ready-to-insert tokens. */
  readonly placeholderOptions = computed<OptionItem[]>(() => [
    ...this.placeholders().map(placeholder => ({
      value: placeholder.value.startsWith('{{')
        ? placeholder.value
        : `{{${placeholder.value}}}`,
      label: placeholder.label,
    })),
    ...this.fields().map(field => ({
      value: `{{field:${field.key}}}`,
      label: localized(field.label, this.lang()),
    })),
  ]);

  /** The `map` row controls are named `key` / `value` whatever the key / value params are called. */
  readonly mapKeyParam = computed<ReflectionParam>(() => ({
    ...this.param().key_param!,
    key: 'key',
  }));
  readonly mapValueParam = computed<ReflectionParam>(() => ({
    ...this.param().value_param!,
    key: 'value',
  }));

  /** Lookup options for stored user ids, named when the label is known and `#id` otherwise. */
  private usersToOptions(ids: unknown): LookupOptionItem[] {
    return userLookupOptions(ids, this.userLabels());
  }

  private readonly arrayControl = computed(
    () => this.group().get(this.param().key) as FormArray | null
  );

  /** Rows of a `map` / `recipients` param; a new array on every change so the template re-renders. */
  readonly rows = computed<FormGroup[]>(() => {
    this.rawValue();
    return [...((this.arrayControl()?.controls as FormGroup[]) ?? [])];
  });

  readonly recipientRows = computed<RecipientRowView[]>(() => {
    const raw = (this.rawValue()[this.param().key] ?? []) as {
      type: ReflectionRecipientTypeEnum | null;
      user_ids: (number | null)[] | null;
    }[];
    return this.rows().map((group, index) => ({
      group,
      index,
      type: raw[index]?.type ?? null,
      selectedUsers: this.usersToOptions(raw[index]?.user_ids),
    }));
  });

  /** Users picked in a `user_list` param, so the lookup opens with them selected. */
  readonly selectedUsers = computed<LookupOptionItem[]>(() =>
    this.usersToOptions(this.rawValue()[this.param().key])
  );

  readonly userLookupApi = REFLECTION_USER_LOOKUP_API;
  readonly userLookupAssignList = computed<AssignList>(() => {
    this.lang();
    return reflectionUserLookupAssignList(key => this.transloco.translate(key));
  });

  addRow(): void {
    const param = this.param();
    const row =
      param.type === ReflectionParamTypeEnum.RECIPIENTS
        ? buildRecipientRow()
        : buildMapRow(param);
    this.arrayControl()?.push(row);
  }

  removeRow(index: number): void {
    this.arrayControl()?.removeAt(index);
  }

  /** Appends the token to the language the admin is typing in. */
  insertPlaceholder(token: string): void {
    const control = this.group().get([this.param().key, this.insertLocale()]);
    if (!control) return;
    const text = (control.value as string) ?? '';
    control.setValue(text ? `${text} ${token}` : token);
    control.markAsDirty();
  }

  private pickPlaceholder(token: string): void {
    this.insertPlaceholder(token);
    // Reset once lib-select has finished handling the click, so the picker shows empty and the same
    // placeholder can be picked again.
    queueMicrotask(() => this.pickerForm.reset());
  }

  private insertLocale(): PlaceholderLocale {
    const lang = this.localizedText()?.currentLang();
    return lang
      ? (lang.toLowerCase() as PlaceholderLocale)
      : this.activeLocale();
  }
}
